import { BrowserQRCodeReader } from '@zxing/browser';
import { useCallback, useEffect, useRef } from 'react';

import { isMediaDevicesSupported } from '@features/Scan/utils';

export const useQrReader = ({
	scanDelay = 150,
	constraints = { video: { facingMode: 'environment' } },
	onResult,
	videoId,
	setError,
	isPaused
}) => {
	const mountedRef = useRef(true);
	const controlsRef = useRef(null);
	const videoElementRef = useRef(null);

	const stopScanning = useCallback(() => {
		if (controlsRef.current) {
			controlsRef.current.stop();
			controlsRef.current = null;
		}
		if (videoElementRef.current?.srcObject) {
			videoElementRef.current.srcObject.getTracks().forEach((track) => track.stop());
			videoElementRef.current.srcObject = null;
			videoElementRef.current = null;
		}
	}, []);

	const startScanning = useCallback(async () => {
		if (!mountedRef.current || isPaused) return;
		stopScanning();

		if (!isMediaDevicesSupported()) {
			setError('Browser does not support camera access');
			return;
		}

		try {
			const codeReader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: scanDelay });
			videoElementRef.current = document.getElementById(videoId);

			if (!videoElementRef.current) throw new Error('Video element not found');

			const controls = await codeReader.decodeFromConstraints(
				{ video: constraints.video },
				videoId,
				(result, error) => {
					if (mountedRef.current && !isPaused) {
						onResult(result || null, error || null);
					}
				}
			);

			if (mountedRef.current) {
				controlsRef.current = controls;
			} else {
				controls.stop();
			}
		} catch {
			setError('Failed to start camera scanning');
		}
	}, [scanDelay, onResult, videoId, constraints, stopScanning, setError, isPaused]);

	useEffect(() => {
		mountedRef.current = true;
		if (!isPaused) startScanning();

		return () => {
			mountedRef.current = false;
			stopScanning();
		};
	}, [startScanning, stopScanning, isPaused]);

	return { stop: stopScanning, reset: startScanning };
};
